import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/solid";
import { useState, Fragment } from "react";
import { Switch } from "@mantine/core";
import useGetDeliveryLocations from "../hooks/useGetDeliveryLocations";
import { useAppDispatch, useAppSelector } from "@store/store";
import { addDeliveryToCart, removePickup } from "@store/slice/cart-slice";

type Props = {
  note?: string;
  setNote: (note: string) => void;
};

const SelectDeliveryCard = ({ note = "", setNote }: Props) => {
  const dispatch = useAppDispatch();
  const { cart } = useAppSelector((state) => state.cart);
  const { data: locationsData, loading, error } = useGetDeliveryLocations();
  const locations = locationsData?.deliveryLocations ?? [];

  const [instantDelivery, setInstantDelivery] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedDeliveryLocation, setSelectedDeliveryLocation] = useState<Record<string, any> | null>(
    cart?.delivery?.location
      ? { location: cart.delivery.location, price: cart.delivery.price, isPayOnline: cart.delivery.isPayOnline }
      : null
  );

  const currency = cart?.products?.[0]?.currency ?? "GHS";

  const onSelectChange = (value: any) => {
    if (!value) return;
    setSelectedDeliveryLocation(value);

    const delivery = {
      id: value.id,
      location: value.location,
      price: instantDelivery && value.hasInstantDelivery
        ? value.instantDeliveryPrice
        : value.price,
      remarks: value.remarks,
      isPayOnline: value.isPayOnline,
      hasInstantDelivery: value.hasInstantDelivery,
      instantDeliveryPrice: value.instantDeliveryPrice,
    };

    dispatch(addDeliveryToCart(delivery as any));
    dispatch(removePickup());
  };

  const filterLocation = (queryValue: string) => {
    const base = instantDelivery
      ? locations.filter((item: any) => item?.hasInstantDelivery)
      : locations;

    if (!queryValue) return base;

    return base.filter((d: any) =>
      d?.location?.toLowerCase().includes(queryValue.toLowerCase())
    );
  };

  return (
    <div className="bg-gray-200 rounded-sm flex-1 p-2 my-2">
      <div className="pb-4">
        <div className="flex items-center justify-between gap-3 bg-gray-200 px-2">
          <span className="font-semibold">Toggle for locations with instant delivery</span>
          <Switch
            checked={instantDelivery}
            onChange={(e) => setInstantDelivery(e.currentTarget.checked)}
            className="bg-gray-400 rounded-2xl p-1"
            size="md"
          />
        </div>

        <Combobox value={selectedDeliveryLocation} onChange={onSelectChange}>
          {({ open }) => (
            <div className="relative w-full mt-4">
              <Combobox.Button className="flex items-center justify-between w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 cursor-pointer focus:outline-none">
                <div className="flex items-start space-x-3">
                  <div className="h-8 w-8 bg-blue-100 flex items-center justify-center rounded-full mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 text-blue-700"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 2.25c3.728 0 6.75 3.022 6.75 6.75 0 4.303-6.75 12.75-6.75 12.75S5.25 13.303 5.25 9c0-3.728 3.022-6.75 6.75-6.75z"
                      />
                      <circle cx="12" cy="9" r="1.5" />
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <p className="text-gray-800 text-sm font-medium">
                      {loading ? "Loading delivery zones..." : "Delivery Zone"}
                    </p>
                    <p className="text-gray-600 text-xs">
                      {selectedDeliveryLocation
                        ? selectedDeliveryLocation.location
                        : "Please select the delivery zone closest to you"}
                    </p>
                  </div>
                </div>
                <span className="text-gray-700 text-sm font-medium bg-gray-100 px-3 py-1.5 rounded-lg">
                  Choose
                </span>
              </Combobox.Button>

              <div className="mt-3">
                <Combobox.Input
                  className="w-full rounded-2xl border border-gray-200 bg-white py-2 px-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  onChange={(event) => setQuery(event.target.value)}
                  displayValue={(location: any) => location?.location ?? ""}
                  placeholder="Search delivery zones"
                />
              </div>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Combobox.Options className="absolute z-10 mt-2 w-full bg-white shadow-lg max-h-60 rounded-xl py-2 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {filterLocation(query).length > 0 ? (
                    filterLocation(query)
                      .filter((d: any) => d != null)
                      .map((d: any) => (
                        <Combobox.Option
                          key={d.id}
                          value={d}
                          className={({ active }) =>
                            `${active ? "bg-blue-100 text-blue-900" : "text-gray-900"} cursor-default select-none relative py-2 pl-3 pr-9`
                          }
                        >
                          {({ active, selected }) => (
                            <div className="flex flex-col">
                              <div className="flex justify-between items-center">
                                <span className={`${selected ? "font-semibold" : "font-normal"} truncate text-sm`}>
                                  {d.location}
                                </span>
                                <span className={`${active ? "text-blue-700" : "text-gray-500"} ml-2 text-sm`}>
                                  {currency}{" "}
                                  {instantDelivery && d.hasInstantDelivery
                                    ? d.instantDeliveryPrice
                                    : d.price}
                                </span>
                              </div>
                              <span className={`${active ? "text-blue-700" : "text-gray-500"} truncate text-xs`}>
                                {d.remarks}
                              </span>
                              {selected && (
                                <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? "text-blue-700" : "text-blue-600"}`}>
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </div>
                          )}
                        </Combobox.Option>
                      ))
                  ) : (
                    <div className="px-4 py-2 text-gray-500 text-sm">
                      {loading ? "Loading..." : "No delivery zones found"}
                    </div>
                  )}
                </Combobox.Options>
              </Transition>

              {error && (
                <span className="mt-1 text-red-600 text-xs">Error loading delivery zones</span>
              )}

              {selectedDeliveryLocation && (
                <div className="flex flex-col gap-2 mt-2 px-1">
                  <span className="text-sm text-gray-500">{selectedDeliveryLocation.remarks}</span>
                  {selectedDeliveryLocation.price != null && (
                    <span className="text-sm font-semibold text-gray-800">
                      {currency} {selectedDeliveryLocation.price}
                    </span>
                  )}
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">Delivery note</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="mt-2 w-full rounded-2xl border border-gray-200 bg-white p-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  rows={3}
                  placeholder="Add notes for the delivery driver"
                />
              </div>
            </div>
          )}
        </Combobox>
      </div>
    </div>
  );
};

export default SelectDeliveryCard;
